import {gql} from '@apollo/client';
import {css} from 'aphrodite';
import React from 'react';

import {ProcessClientOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {TASK_STATUS_ICON_STYLES, TASK_STATUS_ICONS} from 'processes/status/TaskStatusIcon';
import {sortTaskStatuses} from 'processes/status/TaskStatusUtil';
import {MultiValueFilter} from 'search/SearchConstants';
import pickDefined from 'util/pickDefined';

import {Operator} from '../SearchConstants';
import {AllProcessTaskStatusFilterFormQuery} from './__generated/AllProcessTaskStatusFilterFormQuery';
import {
  ProcessTaskStatusFilterFormQuery,
  ProcessTaskStatusFilterFormQueryVariables,
} from './__generated/ProcessTaskStatusFilterFormQuery';
import DropdownFilterForm, {prefixedToggleText} from './base/DropdownFilterForm';
import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';

type ProcessTaskStatus = ProcessTaskStatusFilterFormQuery['processTaskStatusesForClientOrigin'][number];

export const ALL_PROCESS_TASK_STATUSES_FILTER_FORM_QUERY = gql`
  query AllProcessTaskStatusFilterFormQuery @skipAudit @engComponent(name: SIGHTGLASS) {
    processTaskStatuses {
      ... on ProcessTaskStatusNode {
        id
        displayName
        statusType
      }
    }
  }
`;

export const PROCESS_TASK_STATUSES_FOR_CLIENT_ORIGIN_FILTER_FORM_QUERY = gql`
  query ProcessTaskStatusFilterFormQuery($clientOrigin: ProcessClientOrigin!)
  @skipAudit
  @engComponent(name: SIGHTGLASS) {
    processTaskStatusesForClientOrigin(clientOrigin: $clientOrigin) {
      ... on ProcessTaskStatusNode {
        id
        displayName
        statusType
      }
    }
  }
`;

interface ProcessTaskStatusFilterFormProps extends BaseFilterFormProps {
  filter: MultiValueFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: MultiValueFilter) => void;
  operators: Array<Operator>;
  onRemove?: () => void;
}

interface ProcessTaskStatusFilterFormExtraProps {
  processTaskStatuses: Array<ProcessTaskStatus>;
}

export function ProcessTaskStatusFilterForm(
  props: ProcessTaskStatusFilterFormProps & ProcessTaskStatusFilterFormExtraProps
): JSX.Element {
  const optionDatas = sortTaskStatuses(props.processTaskStatuses).map(({id, displayName, statusType}) => ({
    name: displayName,
    icon: TASK_STATUS_ICONS[statusType].icon,
    iconClassName: css(TASK_STATUS_ICON_STYLES[statusType]),
    value: id,
  }));
  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  return (
    <DropdownFilterForm
      {...skeletonProps}
      dropdownToggleText={prefixedToggleText('Status', optionDatas, props.filter)}
      optionDatas={optionDatas}
      filterStyle={props.filterStyle}
      isMulti={true}
      isSearchable={false}
      onRemove={props.onRemove}
      value={props.filter.value}
      onChangeValue={(value: Array<string>) => {
        props.onChangeFilter({...props.filter, value});
      }}
      operators={props.operators}
      selectedOperator={props.filter.operator}
      onChangeOperator={(operator: Operator) => {
        props.onChangeFilter({...props.filter, operator});
      }}
    />
  );
}

export default function QueryingProcessTaskStatusFilterForm(
  props: ProcessTaskStatusFilterFormProps & {clientOrigin?: ProcessClientOrigin}
): JSX.Element {
  if (props.clientOrigin != null) {
    return (
      <BenchlingQuery<ProcessTaskStatusFilterFormQuery, ProcessTaskStatusFilterFormQueryVariables>
        query={PROCESS_TASK_STATUSES_FOR_CLIENT_ORIGIN_FILTER_FORM_QUERY}
        variables={{
          clientOrigin: props.clientOrigin,
        }}
        renderLoading={() => (
          <ProcessTaskStatusFilterForm {...props} isLoading={true} processTaskStatuses={[]} />
        )}
      >
        {(data) => (
          <ProcessTaskStatusFilterForm
            {...props}
            processTaskStatuses={data.processTaskStatusesForClientOrigin}
          />
        )}
      </BenchlingQuery>
    );
  } else {
    return (
      <BenchlingQuery<AllProcessTaskStatusFilterFormQuery>
        query={ALL_PROCESS_TASK_STATUSES_FILTER_FORM_QUERY}
        renderLoading={() => (
          <ProcessTaskStatusFilterForm {...props} isLoading={true} processTaskStatuses={[]} />
        )}
      >
        {(data) => <ProcessTaskStatusFilterForm {...props} processTaskStatuses={data.processTaskStatuses} />}
      </BenchlingQuery>
    );
  }
}

export function getProcessTaskStatusFilterForm(
  clientOrigin: ProcessClientOrigin
): React.ComponentType<ProcessTaskStatusFilterFormProps> {
  return (props: ProcessTaskStatusFilterFormProps) => (
    <QueryingProcessTaskStatusFilterForm {...props} clientOrigin={clientOrigin} />
  );
}

export const ProcedureTaskStatusFilterForm = getProcessTaskStatusFilterForm(ProcessClientOrigin.RECIPES);
export const ProcessAppTaskStatusFilterForm = getProcessTaskStatusFilterForm(ProcessClientOrigin.PROCESSES);
